import React from 'react';
import { 
  Building2, 
  ShoppingBag, 
  Leaf, 
  ShieldCheck, 
  Truck, 
  PhoneCall, 
  MapPin, 
  Clock, 
  Award, 
  CheckCircle2, 
  Sparkles, 
  ArrowRight, 
  MessageCircle, 
  Mail, 
  HeartHandshake, 
  Scale, 
  BookOpen, 
  Users,
  Layers
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const AboutUsPage = ({ onShopClick, onContactClick, onTibbiClick }) => {
  const { lang, t } = useLanguage();
  const { settings, cmsContent } = useStore();

  const hakimPhoto = cmsContent?.hakimProfile?.photoUrl || './images/hakim-shirazi.jpg';

  const pillars = [
    {
      icon: Leaf,
      titleUrdu: '100% خالص و قدرتی جڑی بوٹیاں',
      titleEn: '100% Pure & Organic Botanicals',
      descUrdu: 'ہماری تمام جڑی بوٹیاں پہاڑی و مقامی قدرتی خطوں سے چنی ہوئی، گرد و غبار سے پاک اور کیمیائی اثرات سے مبرا ہوتی ہیں۔',
      descEn: 'Handpicked, dust-free, and chemical-free wild mountain herbs of the highest herbal potency.',
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    {
      icon: Award,
      titleUrdu: 'قانون مفرد اعضاء کے مصدقہ نسخہ جات',
      titleEn: 'Authentic Tibb-e-Pakistani Formulations',
      descUrdu: 'تمام مرکبات، معجون، سفوف، عرقیات اور شربت حضرت صابر ملتانیؒ کے سائنسی و قرابادینی اصولوں پر تیار کیے جاتے ہیں۔',
      descEn: 'Formulated strictly in compliance with the organ-tissue pathology of Qanoon-e-Mufrad Aza.',
      color: 'bg-amber-50 text-amber-800 border-amber-200'
    },
    {
      icon: Scale,
      titleUrdu: 'متنوع اوزان میں فراہمی (1g تا 1kg)',
      titleEn: 'Flexible Weights (1g to 1kg)',
      descUrdu: 'گاہک اپنی ضرورت اور بجٹ کے مطابق 1 گرام، 10 گرام، 50 گرام، 100 گرام، 250 گرام، 500 گرام یا 1 کلو میں آرڈر کر سکتے ہیں۔',
      descEn: 'Order in precise custom quantities tailored to prescription needs and household budgets.',
      color: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    {
      icon: Truck,
      titleUrdu: 'پورے پاکستان میں محفوظ ڈلیوری',
      titleEn: 'Nationwide Safe Courier Delivery',
      descUrdu: 'پاکستان کے تمام شہروں، قصبات اور دیہاتوں میں بذریعہ محفوظ کوریئر ترسیل اور 5,000 روپے سے زائد پر مفت ڈلیوری کی سہولت۔',
      descEn: 'Reliable Cash on Delivery across Pakistan with Free Shipping on all orders above Rs. 5,000.',
      color: 'bg-purple-50 text-purple-700 border-purple-200'
    },
    {
      icon: ShieldCheck,
      titleUrdu: 'طبی سرپرستی و حفظانِ صحت پیکنگ',
      titleEn: 'Clinical Oversight & Hygienic Packing',
      descUrdu: 'فاضل طب والجراحت (FTJ) حکیم مولانا سید عبدالوہاب شاہ شیرازی کی زیر نگرانی حفظانِ صحت کے مطابق پیکنگ۔',
      descEn: 'Prepared and packed under the direct supervision of qualified physician Hakim Syed Abdul Wahab Shah.',
      color: 'bg-teal-50 text-teal-700 border-teal-200'
    },
    {
      icon: HeartHandshake,
      titleUrdu: 'مستقل طبی رہنمائی و سپورٹ',
      titleEn: 'Continuous Medical Guidance',
      descUrdu: 'آرڈر کی فراہمی کے بعد بھی مریضوں اور کسٹمرز کو واٹس ایپ اور فون پر طریقہ استعمال اور پرہیز سے متعلق مفت رہنمائی۔',
      descEn: 'Dedicated post-order patient consultation on dosage, dietary precautions, and lifestyle.',
      color: 'bg-rose-50 text-rose-700 border-rose-200'
    }
  ];

  return (
    <div className="min-h-screen bg-herbal-cream py-6 sm:py-10 font-urdu">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-10">

        {/* 1. HERO BANNER: ABOUT HIMALAYAN PANSAR & DAWAKHANA */}
        <div className="bg-gradient-to-r from-pansar-950 via-pansar-900 to-emerald-950 text-white rounded-3xl p-6 sm:p-12 shadow-xl relative overflow-hidden border border-pansar-800">
          <div className="absolute -right-16 -top-16 w-64 h-64 bg-emerald-600/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -left-16 -bottom-16 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="max-w-3xl text-right relative z-10 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-800/80 border border-emerald-600 text-amber-300 text-xs font-bold shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              <span>تعارفِ ادارہ، فزیکل اسٹور و آن لائن خدمات</span>
            </div>

            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black font-title text-white leading-tight">
              {lang === 'ur' ? settings.storeNameUrdu : settings.storeNameEn}
            </h1>
            
            <p className="text-base sm:text-xl font-bold text-amber-300 font-title">
              {lang === 'ur' ? settings.dawakhanaNameUrdu : settings.dawakhanaNameEn}
            </p>

            <p className="text-xs sm:text-sm text-emerald-100 leading-relaxed max-w-2xl font-urdu">
              اسلام آباد کے سیکٹر ای الیون ٹو (E-11/2) مرکز میں واقع ایک مستند پنسار اسٹور اور پورے پاکستان میں خالص جڑی بوٹیوں، کشیدہ عرقیات، نباتاتی تیل اور قانون مفرد اعضاء کی مصدقہ ادویات کی فراہمی کا بااعتماد ای کامرس پلیٹ فارم۔
            </p>

            <div className="pt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onShopClick}
                className="px-6 py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-pansar-950 font-black text-xs sm:text-sm flex items-center gap-2 shadow-lg transition-all cursor-pointer"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>آن لائن ادویات خریدیے</span>
              </button>

              <button
                type="button"
                onClick={onContactClick}
                className="px-6 py-3 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm flex items-center gap-2 border border-white/20 transition-all cursor-pointer backdrop-blur-sm"
              >
                <MapPin className="w-4 h-4 text-emerald-400" />
                <span>اسٹور و مطب کا پتہ</span>
              </button>

              <a
                href={`https://wa.me/${settings.whatsappNumber || '923470005578'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm flex items-center gap-2 shadow-md transition-all"
              >
                <MessageCircle className="w-4 h-4 text-white" />
                <span>واٹس ایپ طبی مشورہ</span>
              </a>
            </div>
          </div>
        </div>

        {/* 2. SECTION: PHYSICAL STORE SPOTLIGHT (E-11/2 MARKAZ ISLAMABAD) */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Left Col: Store Card Details */}
            <div className="lg:col-span-7 space-y-5 text-right order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold">
                <Building2 className="w-4 h-4 text-emerald-700" />
                <span>فزیکل پنسار اسٹور اور کلینک (اسلام آباد)</span>
              </div>

              <h2 className="text-xl sm:text-3xl font-black text-gray-900 font-title">
                ای الیون ٹو (E-11/2) مرکز، اسلام آباد میں ہمارا اسٹور
              </h2>

              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                اسلام آباد اور راولپنڈی کے معزز شہریوں کے لیے ہمالین پنسار سٹور سیکٹر <strong>E-11/2 مرکز، رائل پلازہ (نزد کے ایف سی)</strong> میں واقع ہے۔ یہاں آپ خود تشریف لا کر ادویات، قدرتی پہاڑی جڑی بوٹیاں، خالص کشیدہ عرقیات اور جڑی بوٹیوں کے تیل اپنی آنکھوں کے سامنے دیکھ کر اور پرکھ کر خرید سکتے ہیں۔
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-3.5 rounded-2xl bg-pansar-50/70 border border-pansar-200/80 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-pansar-700 text-white flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-amber-300" />
                  </div>
                  <div className="text-right">
                    <h4 className="text-xs font-bold text-gray-900">مکمل پتہ</h4>
                    <p className="text-[11px] text-gray-600 leading-normal mt-0.5">
                      {settings.addressUrdu}
                    </p>
                  </div>
                </div>

                <div className="p-3.5 rounded-2xl bg-amber-50/70 border border-amber-200/80 flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-amber-600 text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <div className="text-right">
                    <h4 className="text-xs font-bold text-gray-900">اوقاتِ کار و مطب</h4>
                    <p className="text-[11px] text-gray-600 leading-normal mt-0.5">
                      روزانہ صبح 10:00 بجے تا رات 10:00 بجے (جمعہ نماز کا وقفہ)
                    </p>
                  </div>
                </div>
              </div>

              {/* Action buttons for physical store */}
              <div className="pt-2 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onContactClick}
                  className="px-5 py-2.5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs flex items-center gap-2 shadow cursor-pointer transition-all"
                >
                  <MapPin className="w-3.5 h-3.5 text-amber-300" />
                  <span>گوگل میپ پر لوکیشن دیکھیں</span>
                </button>

                <a
                  href={`tel:${settings.phone || '03470005578'}`}
                  className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs flex items-center gap-2 font-sans transition-all"
                >
                  <PhoneCall className="w-3.5 h-3.5 text-emerald-700" />
                  <span>{settings.phone || '03470005578'}</span>
                </a>
              </div>
            </div>

            {/* Right Col: Store Feature Highlights */}
            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="bg-gradient-to-br from-pansar-900 to-emerald-950 text-white rounded-3xl p-6 sm:p-8 shadow-md space-y-4 border border-pansar-800">
                <h3 className="text-base font-bold text-amber-300 border-b border-pansar-800 pb-2">
                  فزیکل اسٹور کی نمایاں خصوصیات
                </h3>

                <ul className="space-y-3 text-xs text-emerald-50 font-urdu">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <span>مستند معالجِ نبض حکیم مولانا سید عبدالوہاب شاہ سے بالمشافہ طبی تشخیص و معائنہ۔</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <span>خالص ترین پہاڑی جڑی بوٹیوں کی بالمشافہ پرکھ اور تسلی بخش خریداری۔</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <span>دواخانہ میں تیار شدہ تازہ مرکبات (جوارشات، معجون، سفوف، اکسیرات) کی فوری دستیابی۔</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <span>اسلام آباد اور راولپنڈی کے رہائشیوں کے لیے بالمشافہ وزٹ اور فوری پارسل بکنگ۔</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>

        {/* 3. SECTION: E-COMMERCE STORE SERVICES (پورے پاکستان کے لیے ڈیجیٹل سروس) */}
        <div className="bg-gradient-to-br from-emerald-900 via-pansar-950 to-pansar-900 text-white rounded-3xl p-6 sm:p-10 shadow-lg border border-emerald-800/60">
          <div className="max-w-3xl text-right space-y-3 mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400 text-pansar-950 text-xs font-bold">
              <ShoppingBag className="w-3.5 h-3.5" />
              <span>ہمارا ای کامرس آن لائن سٹور</span>
            </div>
            <h2 className="text-xl sm:text-3xl font-black font-title text-white">
              پورے پاکستان میں گھر بیٹھے 100% خالص ادویات کا حصول
            </h2>
            <p className="text-xs sm:text-sm text-emerald-100 leading-relaxed font-urdu">
              وہ حضرات و خواتین جو اسلام آباد سے باہر دوسرے شہروں یا دور دراز دیہاتوں میں مقیم ہیں، ان کے لیے ہمالین پنسار سٹور نے ایک جدید، تیز رفتار اور آسان ای کامرس سسٹم فراہم کیا ہے:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            
            {/* Feature 1 */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 space-y-2.5 text-right">
              <div className="w-10 h-10 rounded-xl bg-amber-400 text-pansar-950 flex items-center justify-center font-bold">
                <Scale className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-white font-title">7 معیاری اوزان میں انتخاب</h3>
              <p className="text-xs text-emerald-100 leading-relaxed">
                ہر جڑی بوٹی اور دوا 1 گرام، 10 گرام، 50 گرام، 100 گرام، 250 گرام، 500 گرام اور 1 کلو کے الگ الگ پیکٹ میں فوری دستیاب ہے۔
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 space-y-2.5 text-right">
              <div className="w-10 h-10 rounded-xl bg-emerald-400 text-pansar-950 flex items-center justify-center font-bold">
                <Truck className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-white font-title">کیش آن ڈلیوری (COD)</h3>
              <p className="text-xs text-emerald-100 leading-relaxed">
                پورے پاکستان میں گھر کی دہلیز پر پارسل وصول کریں۔ <strong>5,000 روپے یا اس سے زائد</strong> کی خریداری پر مکمل فری ڈلیوری!
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 space-y-2.5 text-right">
              <div className="w-10 h-10 rounded-xl bg-teal-400 text-pansar-950 flex items-center justify-center font-bold">
                <BookOpen className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-bold text-white font-title">طبی تشخیصی گائیڈ و فلٹرز</h3>
              <p className="text-xs text-emerald-100 leading-relaxed">
                اعصابی، عضلاتی، غدی تحریکات اور معدہ، دل، جگر، جوڑوں کے امراض کے مطابق درست دوا کی چند سیکنڈ میں خودکار تلاش۔
              </p>
            </div>

          </div>

          <div className="mt-8 text-center sm:text-right">
            <button
              type="button"
              onClick={onShopClick}
              className="px-8 py-3.5 rounded-2xl bg-amber-400 hover:bg-amber-300 text-pansar-950 font-black text-sm inline-flex items-center gap-2 shadow-xl transition-all cursor-pointer"
            >
              <ShoppingBag className="w-4 h-4" />
              <span>سٹور کی تمام مصنوعات براؤز کریں</span>
            </button>
          </div>
        </div>

        {/* 4. SECTION: FOUNDER & HAKIM BIO SPOTLIGHT */}
        <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            {/* Photo */}
            <div className="md:col-span-4 text-center">
              <div className="w-56 h-56 sm:w-64 sm:h-64 mx-auto rounded-3xl overflow-hidden shadow-xl border-4 border-amber-400/50 bg-pansar-50 p-1">
                <img
                  src={hakimPhoto}
                  alt={settings.hakimNameUrdu}
                  className="w-full h-full object-cover object-top rounded-2xl"
                />
              </div>
              <div className="mt-3 inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-950 text-xs font-bold font-title">
                فاضل طب والجراحت (FTJ)
              </div>
            </div>

            {/* Bio Text */}
            <div className="md:col-span-8 space-y-4 text-right">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                <span>طبی سرپرست و بانی</span>
              </div>

              <h2 className="text-2xl sm:text-4xl font-black text-gray-900 font-title">
                {settings.hakimNameUrdu}
              </h2>

              <p className="text-xs sm:text-sm font-bold text-pansar-700 font-title">
                ماہر قانون مفرد اعضاء • دواخانہ طب پاکستانی و ہمالین پنسار سٹور
              </p>

              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                حکیم مولانا سید عبدالوہاب شاہ شیرازی ایک معروف اور کہنہ مشق معالجِ نبض ہیں جنہوں نے اپنی زندگی طبِ پاکستانی (قانون مفرد اعضاء) اور خالص نباتاتی ادویات کی ترویج و اشاعت کے لیے وقف کر رکھی ہے۔ آپ کے دواخانہ اور پنسار سٹور میں تمام ادویات مفرد اعضاء (اعصاب، عضلات، غدد) اور صابر ملتانیؒ کے مصدقہ اصولوں کے عین مطابق دستی اور بغیر کسی کیمیکل کے تیار کی جاتی ہیں۔
              </p>

              <div className="pt-2 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onTibbiClick}
                  className="px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs flex items-center gap-2 shadow cursor-pointer transition-all"
                >
                  <BookOpen className="w-3.5 h-3.5 text-amber-300" />
                  <span>قانون مفرد اعضاء گائیڈ دیکھیں</span>
                </button>

                <a
                  href={`tel:${settings.phone || '03470005578'}`}
                  className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs flex items-center gap-2 font-sans transition-all"
                >
                  <PhoneCall className="w-3.5 h-3.5 text-emerald-700" />
                  <span>فون پر رابطہ: {settings.phone || '03470005578'}</span>
                </a>
              </div>
            </div>

          </div>
        </div>

        {/* 5. SECTION: OUR 6 PILLARS / CORE VALUES */}
        <div className="space-y-6">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="px-3.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
              ہمارے 6 بنیادی اصول و ضمانتیں
            </span>
            <h2 className="text-xl sm:text-3xl font-black text-gray-900 font-title">
              ہمالین پنسار سٹور پر کسٹمرز کا اعتماد کیوں ہے؟
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pillars.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all text-right space-y-3"
                >
                  <div className={`w-12 h-12 rounded-2xl ${item.color} flex items-center justify-center font-bold border`}>
                    <IconComp className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 font-title">
                    {lang === 'ur' ? item.titleUrdu : item.titleEn}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {lang === 'ur' ? item.descUrdu : item.descEn}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 6. FAST CONTACT & ASSISTANCE CARD */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-right">
            <div className="space-y-1">
              <h3 className="text-lg font-black text-gray-900 font-title">
                کسی بھی دوا، جڑی بوٹی یا طبی نسخے سے متعلق رہنمائی درکار ہے؟
              </h3>
              <p className="text-xs text-gray-500 font-urdu">
                ہماری ٹیم اور حکیم صاحب آپ کی بروقت رہنمائی کے لیے ہمہ وقت حاضر ہیں۔
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
              <a
                href={`https://wa.me/${settings.whatsappNumber || '923470005578'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm flex items-center gap-2 shadow-md transition-all"
              >
                <MessageCircle className="w-4 h-4 text-white" />
                <span>واٹس ایپ پر میسج بھیجیں</span>
              </a>

              <a
                href={`tel:${settings.phone || '03470005578'}`}
                className="px-6 py-3 rounded-2xl bg-pansar-800 hover:bg-pansar-900 text-white font-bold text-xs sm:text-sm flex items-center gap-2 shadow-md transition-all font-sans"
              >
                <PhoneCall className="w-4 h-4 text-amber-300" />
                <span>کال کریں: {settings.phone || '03470005578'}</span>
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutUsPage;
